<template>
  <!-- 
    * activity / activity classification
    * historyType
    * - attachment image
    * - attachment file
    * 1 - created
    * 2 - updated
    * 3 - intention
    * 4 - change state
  -->
  <div 
    class="fts-history-content" 
    :class="[
      {'history-content--history': mode === 'history'},
      {'history-content--last': isLast},
      {'history-content--continues': continuesActor}
    ]"
  >
    <!-- actor: rendered once per run of consecutive events by the same person, so six
         actions by one user show one avatar and one name instead of six -->
    <div class="history-content__actor" v-if="!continuesActor">
      <fts-profile
        :user-name="data.by"
        :profile-photo-size="20"
        :profile-name="true"
        class="history-content__actor-profile"
      >
        <template #profileName="{ userProfileName, userProfileUsername }">
          <fts-tippy
            tag="span"
            :content="userProfileUsername"
            append-trigger-class="history-content-by"
          >{{userProfileName}}</fts-tippy>

          <span class="history-content-actor-source" v-if="channel">
            <a
              :href="channel.url"
              target="_blank"
              class="fts-link-primary"
              v-if="channel.url"
            >{{channel.name}}</a>
            <template v-else>{{channel.name}}</template>
          </span>
        </template>
      </fts-profile>
    </div>

    <!-- event: verb, state and provenance on one line. The row itself is the expand
         target when there is a detail panel, so the chevron is an indicator rather than
         a second control, and the timestamp yields to the row actions on hover so
         Reply/Trace are not permanent chrome on every entry. -->
    <div
      class="history-content__event"
      :class="{
        'history-content__event--expandable': hasExpandableDetail,
        'history-content__event--open': isDetailExpand,
        'history-content__event--bare': isEventRowBare
      }"
      @click="onEventClick"
    >
      <div class="history-content-event-gutter">
        <span
          class="material-icons-outlined history-content-event-caret"
          v-if="hasExpandableDetail"
        >chevron_right</span>
      </div>

      <div class="history-content-event-main">
        <!-- actor mark: the TeamMate gets the GNey glyph, every other AI surface keeps
             the "AI" pill. The glyph stands bare rather than boxed in the pill — it is
             a logo, and the house idiom for a branded glyph is bare + gradient class. -->
        <span
          class="history-content-activity-ai"
          :class="{'history-content-activity-ai--teammate': isTeammateAi}"
          v-if="isAIContent"
        >
          <i
            class="fts-icon-teammate inistate-text-teammate"
            role="img"
            aria-label="AI TeamMate"
            v-if="isTeammateAi"
          ></i>
          <span v-else>AI</span>
        </span>

        <template v-if="activity">
          <span class="history-content-activity">performed</span>
          <span class="history-content-activity history-content-activity-title">{{ activity }}</span>
        </template>
        <span
          class="history-content-activity"
          v-else-if="standardActivityLabel"
        >{{ standardActivityLabel }}</span>

        <span
          class="history-content-activity"
          v-if="data.automationRunId"
        >via automation</span>

        <!-- state -->
        <fts-tippy
          tag="span"
          :content="data.state.name"
          :options="{ placement: 'top' }"
          v-if="data.state && data.state.name"
        >
          <fts-state
            :state="data.state"
            :states="states"
            :tint="true"
            class="history-content-state"
          ></fts-state>
        </fts-tippy>

        <!-- approval: the activity went through an approval request an AI agent prepared
             (docs/teammate-human-approval-request-proposal.md). The person who approved is
             still the actor above; this only credits the preparer, with the same "AI" pill
             as an AI row — never the TeamMate glyph, since the preparer may be any agent.
             The name opens the request, the same named route the bell uses (Lite included),
             but only for its recipients and requester: the request page refuses anyone else. -->
        <template v-if="data.approval">
          <span class="history-content-activity">· prepared by</span>
          <span class="history-content-activity-ai"><span>AI</span></span>
          <router-link
            class="history-content-activity history-content-activity-title history-content-approval-link"
            :to="{ name: 'approval-request', params: { id: data.approval.id } }"
            @click.native.stop
            v-if="data.approval.canOpen"
          >{{ data.approval.preparedBy }}</router-link>
          <span
            class="history-content-activity history-content-activity-title"
            v-else
          >{{ data.approval.preparedBy }}</span>
        </template>

        <!-- ai: the ring is the only place the confidence score is shown — the expanded
             panel deliberately does not restate it. The tooltip carries the band word
             ("Very High confidence"), which is just the number's interpretation and so
             belongs beside the number. Expanding is the row's job, not the ring's. -->
        <fts-tippy
          tag="div"
          class="history-content-confidence-tip"
          :content="aiConfidenceTooltip"
          append-trigger-class="history-content-confidence"
          v-if="isAIContent"
        >
          <span
            :style="{ color: aiConfidenceScoreColor }"
            class="material-symbols-outlined material-symbols-outlined--filled history-content-confidence-icon"
            v-if="showAIConfidenceWarning"
          >warning</span>

          <div class="history-content-confidence-marks" :style="{ color: aiConfidenceScoreColor }" ref="aiConfidence">
            <svg class="history-content-confidence-ring" viewBox="0 0 44 44" aria-hidden="true">
              <circle class="history-content-confidence-ring-track" cx="22" cy="22" r="18"></circle>
              <circle
                class="history-content-confidence-ring-progress"
                cx="22"
                cy="22"
                r="18"
                :style="{
                  stroke: aiConfidenceScoreColor,
                  strokeDasharray: aiConfidenceCircleCircumference,
                  strokeDashoffset: aiConfidenceCircleOffset
                }"
              ></circle>
            </svg>

            <div
              class="history-content-confidence-value"
              :class="{ 'history-content-confidence-value--compact': aiConfidencePercentageDisplay === 100 }"
            >{{aiConfidencePercentageDisplay}}</div>
          </div>
        </fts-tippy>

        <!-- the panel this opens is the row itself, so the change count is a quiet
             numeral rather than a labelled button -->
        <span
          class="history-content-change-count"
          v-if="hasChanges"
        >{{ data.changes.length }}</span>
      </div>

      <div class="history-content-event-aside">
        <fts-tippy
          tag="span"
          class="history-content-event-time"
          :content="createdDateLabel"
        >{{createdTime}}</fts-tippy>

        <div class="history-content-event-actions" @click.stop>
          <!-- reaction picker -->
          <fts-popper
            placement="left"
            :offset="[0, 6]"
            content-class="history-content-reaction-dropdown"
            class="history-content-reaction-toggle"
            v-if="!isEditingComment && !isCommentDeleted"
          >
            <div slot="reference" class="history-content-reaction-toggle-ref">
              <span class="material-icons-outlined">emoji_emotions</span>
            </div>

            <template #content="{ select }">
              <div
                v-for="x in reactions"
                :key="`reaction-${x.id}`"
                class="history-content-reaction-option"
                @click="selectReaction(select, x.emoji)"
              >
                <fts-moji :emoji="x.emoji" :size="14" />
              </div>
            </template>
          </fts-popper>

          <!-- icons, not labels: the actions share a grid cell with the timestamp, so the
               cell is only as wide as the wider of the two. Text labels would reserve
               ~90px on every row and push the verb into wrapping. -->
          <fts-tippy
            tag="div"
            content="Reply"
            append-trigger-class="history-content-event-action"
            @trigger:click="onReply"
            v-if="!isEditingComment && !isCommentDeleted"
          >
            <span class="material-icons-outlined">reply</span>
          </fts-tippy>

          <fts-tippy
            tag="div"
            content="Trace"
            append-trigger-class="history-content-event-action"
            @trigger:click="openTrace"
            v-if="canViewTrace"
          >
            <span class="material-icons-outlined">manage_search</span>
          </fts-tippy>

          <!-- action menu -->
          <!-- Copy text stays internal/lite-only; Edit and Delete are for every member the
               server allows (docs/collaboration-comment-edit-delete-proposal.md §7.1) -->
          <div
            class="history-content-action-container"
            v-if="
              mode !== 'history' &&
              !isCommentDeleted &&
              (
                ((isAlpha || isLite) && isHistoryContent) ||
                isCommentEditable ||
                isCommentDeletable ||
                canViewRevisions
              )
            "
          >
            <fts-dropdown-select
              append-content-class="dropdown-menu dropdown-menu-w-xs dropdown-menu--lite"
            >
              <div
                slot="toggle"
                slot-scope="{ isShow }"
                class="history-content-action-btn"
                :class="{'history-content-action-btn--active': isShow}"
              >
                <span class="material-icons-outlined">more_vert</span>
              </div>

              <template #menu="{ select }">
                <div
                  class="dropdown-option dropdown-option--lite"
                  v-clipboard:copy="data.content"
                  v-clipboard:success="evt => clipboardOnSuccess(evt, select)"
                  v-clipboard:error="clipboardOnError"
                  v-if="(isAlpha || isLite) && isHistoryContent"
                >
                  <div class="dropdown-option-icon">
                    <span class="material-icons-outlined">content_copy</span>
                  </div>
                  <div>Copy text</div>
                </div>

                <div
                  class="dropdown-option dropdown-option--lite"
                  @click="onEdit(select)"
                  v-if="isCommentEditable"
                >
                  <div class="dropdown-option-icon">
                    <span class="material-icons-outlined">edit</span>
                  </div>
                  <div>Edit</div>
                </div>

                <div
                  class="dropdown-option dropdown-option--lite dropdown-option--danger"
                  @click="onDelete(select)"
                  v-if="isCommentDeletable"
                >
                  <div class="dropdown-option-icon">
                    <span class="material-icons-outlined">delete</span>
                  </div>
                  <div>Delete</div>
                </div>
                <!-- P3 admin "view original": the server-side revision trail (§12 P3) -->
                <div
                  class="dropdown-option dropdown-option--lite"
                  @click="onViewRevisions(select)"
                  v-if="canViewRevisions"
                >
                  <div class="dropdown-option-icon">
                    <span class="material-icons-outlined">history</span>
                  </div>
                  <div>View original</div>
                </div>
              </template>
            </fts-dropdown-select>
          </div>
        </div>

      </div>
    </div>

    <!-- tombstone: keeps the slot, names the admin when it wasn't the author (§7.3) -->
    <div class="history-content__body" v-if="isCommentDeleted">
      <div class="history-content-comment history-content-comment--deleted">
        <span class="material-icons-outlined history-content-comment-deleted-icon">block</span>
        <span>{{ deletedLabel }}</span>
        <!-- tombstones have no ⋮ menu; admins get the trail from here -->
        <span class="history-content-revisions-link" @click="onViewRevisions()" v-if="canViewRevisions">View original</span>
      </div>
    </div>

    <div class="history-content__body" v-else-if="isEditingComment">
      <fts-comment
        :vector-id="vectorId"
        :comment="formData"
        :initial-comment="true"
        :attachments-locked="true"
        :scrollIntoViewOptions="{
          block: 'nearest'
        }"
        :isCancelEnabled="true"
        :isSubmitEnabled="true"
        ref="comment"
        @comment:cancel="onCancelEdit"
        @comment:submit="onSubmitComment"
      ></fts-comment>
    </div>

    <template v-else>
      <!-- attachment -->
      <div class="history-content__body" v-if="data.attachments && data.attachments.length">
        <fts-file 
          :prefixURL="`${vectorId}-comment/`" 
          :data="data.attachments" 
          :downloadable="true" 
          :audit="{
            userName: data.by,
            date: data.on,
          }" 
          :description="translateContent" 
          class="history-content-attachment"
        ></fts-file>
      </div>

      <!-- content:comment — a comment, or the words typed alongside an activity. Same
           authored text, same slot, same treatment. -->
      <div class="history-content__body" v-if="isHistoryContent">
        <div class="history-content-comment">
          <span v-html="translateContent" ref="comment"></span>
          <span class="history-content-comment-edited" :title="editedStamp" v-if="!!data.editedOn">(edited)</span>
        </div>
      </div>
    </template>

    <!-- reactions stay permanently visible: they are content, not chrome. The reaction
         picker, Reply and Trace moved into the event row's hover actions. -->
    <div class="history-content__footer" v-if="!!reactionList">
      <div class="history-content-reaction-gap">
        <fts-tippy
          v-for="(val, emoji) in reactionList"
          :key="emoji"
          append-box-class="tippy-box-history-reaction"
          append-trigger-class="history-content-reaction"
          append-content-class="tippy-content-history-reaction"
          class="history-content-reaction-item"
          :class="{'history-content-reaction-item--active': isReactByCurrentUser(val)}"
          @trigger:click="selectReaction(null, emoji)"
        >
          <fts-moji :emoji="emoji" :size="12" />
          <div class="history-content-reaction-count">
            {{val.length}}
          </div>

          <template #content>
            <fts-moji
              :emoji="emoji"
              :size="32"
              class="history-reaction-emoji"
            />
            <div class="history-reaction-user-container">
              <fts-profile
                v-for="user in val"
                :key="user.username"
                :user-name="user.username"
                class="history-reaction-user"
              />
              <div class="history-reaction-name" v-if="getReactionName(emoji)">
                reacted with {{getReactionName(emoji)}}
              </div>
            </div>
          </template>
        </fts-tippy>
      </div>
    </div>

    <!-- content:detail (inline, expandable) — the AI summary and the change log open together
         as ONE panel, from either toggle (confidence ring or Changes). AI first — it explains
         the change; the concrete log sits below it, rendered as-is. -->
    <!-- P3 admin "view original": the comment's revision trail, oldest first. Edits show as a
         text diff against the next version; a delete shows the text (and attachments) removed. -->
    <div class="history-content__revisions" v-if="showRevisions">
      <div class="history-content-revisions-header">
        <span class="material-icons-outlined">history</span>
        <span class="history-content-revisions-title">Comment history</span>
        <span class="material-icons-outlined history-content-revisions-close" @click="showRevisions = false">close</span>
      </div>
      <div class="history-content-revisions-loading" v-if="revisionsLoading">Loading…</div>
      <template v-else-if="revisions">
        <div class="history-content-revision" v-for="(rev, idx) in revisions.revisions" :key="`rev-${idx}`">
          <div class="history-content-revision-caption">
            {{ rev.kind === 'delete' ? 'Deleted' : 'Edited' }} by {{ rev.by }} · {{ formatStamp(rev.on) }}
          </div>
          <template v-if="rev.kind === 'delete'">
            <div class="history-content-revision-text">{{ rev.content }}</div>
            <div class="history-content-revision-attachments" v-if="rev.attachments && rev.attachments.length">
              Attachments: {{ rev.attachments.map(a => a.name || a.path).join(', ') }}
            </div>
          </template>
          <fts-history-text-diff v-else :original="rev.content || ''" :current="revisionNext(idx) || ''" />
        </div>
        <div class="history-content-revisions-loading" v-if="!revisions.revisions || !revisions.revisions.length">No changes recorded.</div>
      </template>
    </div>

    <fts-transition-expand v-if="hasExpandableDetail">
      <div class="history-content__detail" v-if="isDetailExpand">
        <!-- ai -->
        <div class="history-content__ai" v-if="isAIContent">
          <div class="history-content-ai-header">
            <!-- the in-app TeamMate gets its own mark; every other AI surface (the MCP
                 server, which never stamps ai.agent) keeps the generic sparkle -->
            <div class="history-content-ai-icon">
              <i
                class="fts-icon-teammate inistate-text-teammate"
                aria-hidden="true"
                v-if="isTeammateAi"
              ></i>
              <img :src="aiSparkleIcon" alt="" aria-hidden="true" v-else />
            </div>

            <!-- model names are long and arbitrary ("deepseek/deepseek-v4-flash"): the line
                 ellipsizes and the full value lives in the tooltip, so a long name can never
                 shatter across several lines -->
            <fts-tippy
              tag="span"
              class="history-content-ai-model"
              :content="aiModelLabel"
              append-trigger-class="history-content-ai-model-text"
              v-if="data.ai.model"
            >{{data.ai.model}}<span
              class="history-content-ai-model-version"
              v-if="aiModelVersion"
            >{{aiModelVersion}}</span></fts-tippy>
          </div>

          <div class="history-content-ai-body">
            <div
              class="history-content-ai-reason"
              v-html="data.ai.reasoning"
              v-if="data.ai.reasoning"
            ></div>

            <template v-if="aiSources.length">
              <div class="history-content-ai-source-legend">SOURCES</div>

              <div class="history-content-ai-source-container">
                <div
                  v-for="(source, sourceIndex) in aiSources"
                  :key="sourceIndex"
                  class="history-content-ai-source"
                  :class="source.typeClass"
                >
                  <div class="history-content-ai-source-header">
                    <div class="history-content-ai-source-icon">
                      <span class="material-symbols-outlined">{{source.typeIcon}}</span>
                    </div>

                    <div class="history-content-ai-source-badge">{{source.displayType}}</div>
                  </div>

                  <div class="history-content-ai-source-reference">{{source.reference}}</div>
                  <div class="history-content-ai-source-excerpt">{{source.excerpt}}</div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- changes (calm-density log — content untouched, the panel provides the box) -->
        <div class="history-content__changes" v-if="hasChanges">
          <fts-history-log :module-id="vectorId" :log="data.changes" />
        </div>
      </div>
    </fts-transition-expand>

    <!-- trace -->
    <trace-modal
      append-to-body
      :requestId="data.requestId"
      :historyId="data.id"
      ref="traceModal"
      v-if="canViewTrace"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import VueClipboard from 'vue-clipboard2';
Vue.use(VueClipboard);

import { authorizationStore } from '../../store/authorizationStore';
import { classificationStore } from '../../store/classificationStore';
import commons from '../../commons/commons';
import { htmlValidator, commentMentionsValidator } from '../../commons/validator';
import aiSparkleIcon from '../../../assets/images/ai/ai-sparkle.svg';

import FTSBtn from '../FTSBtn';
import FTSComment from '../FTSComment';
import FTSDropdownSelect from '../FTSDropdownSelect';
import FTSFile from '../file/FTSFile';
import FTSHistoryLog from './FTSHistoryLog';
import FTSHistoryTextDiff from './FTSHistoryTextDiff';
import FTSMoji from '../FTSMoji';
import FTSPopper from '../FTSPopperNew';
import FTSProfile from '../FTSProfile';
import FTSState from '../FTSState';
import FTSTippy from '../FTSTippy';
import FTSTransitionExpand from '../FTSTransitionExpand';
import TraceModal from './TraceModal.vue';

// Standard (non-custom) activity verbs, keyed by LocusDetailHistoryType. A type with no
// entry here falls back to the plain-comment wording.
const STANDARD_ACTIVITY_LABEL = Object.freeze({
  1: 'created this',
  2: 'updated this',
  4: 'changed state',
  7: 'has done assigned',
  8: 'imported this',
  9: 'merged this',
  12: 'managed this',
});

const AI_SOURCE_TYPE_META = Object.freeze({
  default: {
    icon: 'error',
    modifier: null,
  },
  code: {
    icon: 'code',
    modifier: 'code',
  },
});

export default {
  props: {
    data: Object,
    child: Boolean,
    vectorId: [String, Number],
    locusId: [String, Number],
    states: Array,
    users: Array,
    commentSection: {
      type: Boolean,
      default: true,
    },
    mode: String,
    isLast: Boolean,
    isLite: Boolean,
    // set by the list when the previous entry has the same actor on the same day:
    // the avatar and name are then suppressed and only the event line renders
    continuesActor: Boolean
  },
  components: {
    "trace-modal": TraceModal,
    "fts-btn": FTSBtn,
    "fts-comment": FTSComment,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-file": FTSFile,
    "fts-history-log": FTSHistoryLog,
    "fts-history-text-diff": FTSHistoryTextDiff,
    "fts-moji": FTSMoji,
    "fts-profile": FTSProfile,
    "fts-popper": FTSPopper,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy,
    "fts-transition-expand": FTSTransitionExpand
  },
  data: () => ({
    aiSparkleIcon,
    reactions: [
      { id: "thumbsup", emoji: "👍", name: ":thumbsup:" },
      { id: "celebration", emoji: "🎉", name: ":tada:" },
      { id: "biceps", emoji: "💪", name: ":muscle:" },
      { id: "heart", emoji: "❤", name: ":heart:" },
      { id: "raised", emoji: "🙌", name: ":raised_hands:" },
      { id: "joy", emoji: "😂", name: ":joy:" },
    ],
    formData: null,
    isEditingComment: false,
    // P3 admin "view original"
    revisions: null,
    revisionsLoading: false,
    showRevisions: false,
    // one flag for the shared detail panel (AI summary + change log) — the confidence
    // ring and the Changes toggle both drive it
    isDetailExpand: false,
    isAIConfidenceRingReady: false
  }),
  methods: {
    moment,
    initAiConfidenceRingAnimation() {
      if (!this.isAIContent || this.isAIConfidenceRingReady) {
        return;
      }

      const target = this.$refs.aiConfidence;
      if (!target) {
        return;
      }

      if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
        this.startAiConfidenceRingAnimation();
        return;
      }

      this._aiConfidenceRingObserver = new window.IntersectionObserver((entries) => {
        const isVisible = entries.some(entry => entry.isIntersecting || entry.intersectionRatio > 0);
        if (!isVisible) {
          return;
        }

        this.startAiConfidenceRingAnimation();
      }, {
        threshold: 0.2,
      });

      this._aiConfidenceRingObserver.observe(target);
    },
    startAiConfidenceRingAnimation() {
      if (this.isAIConfidenceRingReady) {
        return;
      }

      if (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.cleanupAiConfidenceRingObserver();
        this.isAIConfidenceRingReady = true;
        return;
      }

      this.cleanupAiConfidenceRingObserver();
      this._aiConfidenceRingFrame = window.requestAnimationFrame(() => {
        this._aiConfidenceRingFrame = window.requestAnimationFrame(() => {
          this.isAIConfidenceRingReady = true;
          this._aiConfidenceRingFrame = null;
        });
      });
    },
    cleanupAiConfidenceRingObserver() {
      if (this._aiConfidenceRingObserver) {
        this._aiConfidenceRingObserver.disconnect();
        this._aiConfidenceRingObserver = null;
      }
    },
    cleanupAiConfidenceRingAnimation() {
      this.cleanupAiConfidenceRingObserver();

      if (this._aiConfidenceRingFrame) {
        window.cancelAnimationFrame(this._aiConfidenceRingFrame);
        this._aiConfidenceRingFrame = null;
      }
    },
    // The whole event row is the expand target, so the chevron and the confidence ring
    // are indicators rather than separate controls. The actions cluster stops propagation
    // so Reply/Trace/the menu never toggle the panel.
    onEventClick() {
      if (this.hasExpandableDetail) {
        this.isDetailExpand = !this.isDetailExpand;
      }
    },
    selectReaction(callback, emoji) {
      if (typeof callback === "function") {
        callback();
      }

      const userName = authorizationStore.state.userName;
      let reaction = this.data.reactions && this.data.reactions.find(
        (x) => x.emoji === emoji && x.username === userName
      );
      if (reaction) {
        var ind = this.data.reactions.indexOf(reaction);
        this.data.reactions.splice(ind, 1);
      } else {
        if (!this.data.reactions) {
          this.$set(this.data, "reactions", []);
        }
        this.data.reactions.push({
          emoji: emoji,
          username: userName,
        });
      }

      classificationStore.dispatch("reactActivity", {
        emoji: emoji,
        history: this.data.history,
        locusDetailId: this.data.history ? this.data.id : null,
        locusCollaborationId: this.data.history ? null : this.data.id,
        id: this.data.entryId,
      });
    },
    getReactionName(emoji) {
      const reaction = this.reactions.find(x => x.emoji === emoji);
      return reaction && reaction.name;
    },
    clipboardOnSuccess(event, callback) {
      if (typeof callback === "function") {
        callback();
      }
      
      this.$alertSuccess("Comment copied to clipboard.");
    },
    clipboardOnError(error) {
      console.error(error);
      this.$alertError("Failed copy comment");
    },
    onEdit(callback) {
      if (typeof callback === "function") {
        callback();
      }

      this.formData = _.cloneDeep(this.data);
      
      if (this.formData.content) {
        try {
          const matches = commentMentionsValidator(this.formData.content);
          if (matches) {
            const mentions = [...new Set(matches)];
            mentions.forEach(mention => {
              this.formData.content = this.formData.content.replaceAll(
                mention,
                `<span class="mention" data-mention-id="${mention.substring(1)}">${mention}</span>`
              );
            });
          }
        } catch(err) {
          console.error(err);
        }
        this.formData.content = this.formData.content.replaceAll('&nbsp;', ' ').replace(/\r/g, '');
        const convertToParagraphs = (text) => {
          const lines = text.split('\n');
          return lines.map(line => `<p>${line}</p>`).join('');
        }
        this.formData.content = convertToParagraphs(this.formData.content);
      }

      this.isEditingComment = true;

      this.$nextTick(() => {
        this.$refs.comment.onFocus();
      });
    },
    onCancelEdit() {
      this.isEditingComment = false;
      this.formData = null;
    },
    // The server answers a refused edit/delete with 403 { reason, error } (§5); word the toast
    // from the reason so the user learns the rule, not just "failed".
    commentErrorMessage(error, fallback) {
      const data = error && error.response && error.response.data;
      const reason = data && data.reason;
      switch (reason) {
        case 'editWindowExpired':
          return 'Comments can only be edited within 15 minutes of posting.';
        case 'deleteWindowExpired':
          return 'Comments can only be deleted within 60 hours of posting.';
        case 'notOwner':
          return 'You can only change your own comments.';
        case 'alreadyDeleted':
          return 'This comment was already deleted.';
        case 'emptyContent':
          return 'A comment cannot be emptied. Delete it instead.';
        case 'noCommentRight':
          return 'You do not have the comment right on this module.';
        case 'archived':
          return 'This comment can no longer be changed.';
        case 'notAdmin':
          return "Only workspace admins can view a comment's history.";
      }

      return fallback;
    },
    async onSubmitComment() {
      const submitData = _.cloneDeep(this.formData);
      const before = _.cloneDeep(this.data);
      let content = submitData.content.replace(/<br\s*\/?>/gi, '\n');
      content = content.replace(/<\/p>(?=[\s\S]*<\/p>)/gi, '\n');
      content = content.replace(/<[^>]+>/g, '');
      content = content.trim();

      this.isEditingComment = false;
      this.formData = null;

      if (content === (before.content || '')) {
        return;
      }

      // optimistic; the server's editedOn replaces the placeholder on success, and the
      // previous text comes back on failure
      this.$emit("update:data", {...before, content: content, editedOn: moment().format()});

      try {
        // text only (R5): attachments are never sent on an edit
        const res = await classificationStore.dispatch("sendComment", {
          id: this.data.id,
          content: submitData.content,
          locusId: this.data.entryId || this.locusId,
          type: !!this.data.comment ? 1 : 0
        });
        const saved = res && res.data ? res.data : res;
        this.revisions = null;   // the trail grew; reload it on the next "View original"
        this.$emit("update:data", {
          ...before,
          content: saved && saved.content != null ? saved.content : content,
          editedOn: saved && saved.editedOn ? saved.editedOn : before.editedOn || null,
          hasRevisions: true
        });
      } catch(error) {
        console.error(error);
        this.$emit("update:data", before);
        this.$toasted.global.error({
          response: error.response,
          message: this.commentErrorMessage(error, 'Failed to update comment')
        });
      }
    },
    async onDelete(callback) {
      if (typeof callback === "function") {
        callback();
      }

      // §7.2 copy; the extra line only when an admin removes someone else's comment
      const isAdminDelete = this.data.by !== this.currentUsername;
      let text = "This comment will be removed for everyone in this entry. Replies stay.";
      if (isAdminDelete) {
        text += ` Deleting as workspace admin — ${this.data.by} will see that you removed it.`;
      }

      const res = await this.$confirmation.show({
        isLite: true,
        type: "delete",
        title: "Delete comment?",
        text: text
      });

      if (res === true) {
        const before = _.cloneDeep(this.data);

        // optimistic tombstone; rolled back if the server refuses
        this.$emit("update:data", {
          ...before,
          deleted: true,
          deletedBy: this.currentUsername,
          deletedOn: moment().format(),
          content: null,
          attachments: null,
          reactions: null,
          can: null,
          hasRevisions: true
        });
        this.revisions = null;
        this.showRevisions = false;

        try {
          const res = await classificationStore.dispatch("sendComment", {
            id: this.data.id,
            locusId: this.data.entryId || this.locusId,
            type: !!this.data.comment ? 1 : 0,
            delete: true
          });
          const saved = res && res.data ? res.data : res;
          if (saved && saved.deletedOn) {
            this.$emit("update:data", {...this.data, deletedBy: saved.deletedBy, deletedOn: saved.deletedOn});
          }
          this.$alertSuccess("Comment deleted.");
          // "View original" is offered on the optimistic tombstone, so a trail opened while the delete
          // was in flight was loaded without its Delete revision: drop it, and reload it if it is open.
          this.revisions = null;
          if (this.showRevisions) {
            await this.loadRevisions();
          }
        } catch(error) {
          console.error(error);
          this.$emit("update:data", before);
          this.$toasted.global.error({
            response: error.response,
            message: this.commentErrorMessage(error, "Failed to delete comment")
          });
        }
      }
    },
    // P3 admin "view original" (§12 P3): toggle the inline revision trail; loaded once per item.
    async onViewRevisions(callback) {
      if (typeof callback === "function") {
        callback();
      }

      this.showRevisions = !this.showRevisions;
      if (this.showRevisions && !this.revisions) {
        await this.loadRevisions();
      }
    },
    async loadRevisions() {
      this.revisionsLoading = true;
      try {
        const res = await classificationStore.dispatch("loadCommentRevisions", {
          id: this.data.id,
          type: !!this.data.comment ? 1 : 0,
          locusId: this.data.entryId || this.locusId
        });
        this.revisions = res && res.data ? res.data : res;
      } catch (error) {
        console.error(error);
        this.showRevisions = false;
        this.$toasted.global.error({
          response: error.response,
          message: this.commentErrorMessage(error, 'Failed to load comment history')
        });
      } finally {
        this.revisionsLoading = false;
      }
    },
    // the text a revision was replaced BY: the next revision's stored text, or the live text
    revisionNext(index) {
      const list = (this.revisions && this.revisions.revisions) || [];
      if (index + 1 < list.length) {
        return list[index + 1].content;
      }
      return this.revisions ? this.revisions.content : null;
    },
    formatStamp(on) {
      return on ? moment(on).format('lll') : '';
    },
    onReply() {
      let content = "<p></p>";

      if (Boolean(this.data && this.data.by) && this.data.by !== this.currentUsername) {
        content = `<p><span class="mention" data-mention-id="${this.data.by}">@${this.data.by}</span> </p>`;
      }

      this.$emit("comment:reply", content);
    },
    isWithinTimeLimit(datetime, unit, limit) {
      return moment().diff(datetime, unit) <= limit;
    },
    openTrace() {
      //alert(this.data.requestId);
      this.$refs.traceModal.show();
    },
    isReactByCurrentUser(list) {
      return list.some(x => x.username == this.currentUsername);
    },
  },
  computed: {
    isAlpha() {
      return authorizationStore.getters.isInternal;
    },
    canViewTrace() {
      if (this.isLite) return false;
      return authorizationStore.getters.isAdminUser;
    },
    currentUsername() {
      return authorizationStore.getters.userName;
    },
    // The day is carried by the list's date divider, so the row shows only the clock
    // time; the full stamp and the channel live in its tooltip.
    createdTime() {
      return this.data.on ? moment(this.data.on).format('LT') : null;
    },
    createdDateLabel() {
      if (!this.data.on) {
        return null;
      }

      const stamp = moment(this.data.on).format('lll');
      return this.channel ? `${stamp} · via ${this.channel.name}` : stamp;
    },
    isComment() {
      return this.data.content || (this.data.attachments && this.data.attachments.length);
    },
    isCommentDeleted() {
      return this.data && this.data.deleted === true;
    },
    isHistoryContent() {
      return htmlValidator(this.data.content);
    },
    // With the comment verb gone, a plain comment's event row holds nothing but the
    // timestamp — it exists to host the row actions, not to be seen. Painting a hover
    // band across an empty strip reads as a broken row, so a bare row skips it and lets
    // the comment underneath carry the highlight instead.
    isEventRowBare() {
      return !this.isAIContent &&
        !this.activity &&
        !this.standardActivityLabel &&
        !this.data.automationRunId &&
        !(this.data.state && this.data.state.name) &&
        !this.data.approval &&
        !this.hasChanges;
    },
    isAIContent() {
      return this.data.ai;
    },
    hasChanges() {
      return Boolean(this.data.changes && this.data.changes.length);
    },
    hasExpandableDetail() {
      return Boolean(this.isAIContent) || this.hasChanges;
    },
    // The platform stamps ai.agent = "teammate" for the in-app TeamMate (a constant, see
    // AITeammateService.BuildAiStamp) and leaves it absent on the MCP-server path.
    isTeammateAi() {
      const agent = this.data && this.data.ai && this.data.ai.agent;
      return typeof agent === 'string' && agent.trim().toLowerCase() === 'teammate';
    },
    // Providers sometimes stamp model and modelVersion with the same string — rendering
    // both just doubles the width of an already long name for no information.
    aiModelVersion() {
      const ai = this.data && this.data.ai;
      if (!ai || !ai.modelVersion || ai.modelVersion === ai.model) {
        return null;
      }

      return ai.modelVersion;
    },
    // the untruncated value, for the tooltip on the ellipsized header line
    aiModelLabel() {
      const ai = this.data && this.data.ai;
      if (!ai) {
        return null;
      }

      return [ai.model, this.aiModelVersion].filter(Boolean).join(' · ');
    },
    aiSources() {
      const sources = this.data && this.data.ai && Array.isArray(this.data.ai.sources)
        ? this.data.ai.sources
        : [];

      return sources.map(source => {
        const normalizedType = typeof source.type === 'string'
          ? source.type.trim().toLowerCase()
          : '';
        const typeMeta = AI_SOURCE_TYPE_META[normalizedType] || AI_SOURCE_TYPE_META.default;

        return Object.assign({}, source, {
          displayType: source.type || 'unknown',
          typeClass: typeMeta.modifier ? `history-content-ai-source--${typeMeta.modifier}` : null,
          typeIcon: typeMeta.icon,
        });
      });
    },
    aiConfidencePercentage() {
      if (!this.data || !this.data.ai || typeof this.data.ai.confidence !== 'number') {
        return;
      }

      return this.data.ai.confidence * 100;
    },
    aiConfidencePercentageValue() {
      if (typeof this.aiConfidencePercentage !== 'number') {
        return;
      }

      return Math.max(0, Math.min(this.aiConfidencePercentage, 100));
    },
    aiConfidencePercentageDisplay() {
      if (typeof this.aiConfidencePercentageValue !== 'number') {
        return;
      }

      return Math.round(this.aiConfidencePercentageValue);
    },
    aiConfidenceScore() {
      if (typeof this.aiConfidencePercentage !== 'number') {
        return;
      }

      if (this.aiConfidencePercentage >= 95) {
        return "Very High";
      }

      if (this.aiConfidencePercentage >= 70) {
        return "High";
      }

      if (this.aiConfidencePercentage >= 56) {
        return "Medium";
      }

      if (this.aiConfidencePercentage >= 30) {
        return "Low";
      }

      return "Very Low";
    },
    // the band word lives here rather than in the panel — it is the number's
    // interpretation, so it reads next to the number the ring already shows
    aiConfidenceTooltip() {
      return this.aiConfidenceScore
        ? `${this.aiConfidenceScore} confidence`
        : 'AI details';
    },
    aiConfidenceScoreColor() {
      switch (this.aiConfidenceScore) {
        case "Very High":
          return "var(--inistate-color-primary-500)";
        case "High":
          return "var(--inistate-icon-success)";
        case "Medium":
          return "var(--inistate-icon-warning)";
        case "Low":
        case "Very Low":
          return "var(--inistate-icon-danger)";
        default:
          return;
      }
    },
    aiConfidenceCircleCircumference() {
      const radius = 18;

      return 2 * Math.PI * radius;
    },
    aiConfidenceCircleOffset() {
      if (typeof this.aiConfidencePercentageValue !== 'number') {
        return;
      }

      if (!this.isAIConfidenceRingReady) {
        return this.aiConfidenceCircleCircumference;
      }

      return this.aiConfidenceCircleCircumference * (1 - this.aiConfidencePercentageValue / 100);
    },
    showAIConfidenceWarning() {
      return false;
    },
    translateContent() {
      if (this.isHistoryContent) {
        return commons.translateCommentContent(this.data.content, this.users);
      }
    },
    reactionList() {
      const res = _.groupBy(this.data.reactions, "emoji");
      return !_.isEmpty(res) && res;
    },
    standardActivityType() {
      return this.data.type ? parseInt(this.data.type) : 0;
    },
    activity() {
      return this.data.activity;
    },
    // The nine verb branches this replaces were identical spans differing only in text.
    // A custom `activity` takes precedence and is rendered separately (it needs the bold
    // title span), so this only covers the standard types.
    //
    // A plain comment gets no verb at all: the comment is rendered directly underneath,
    // which says "commented" far better than the word did, and replies have never
    // carried one. Only doing-something events need naming.
    standardActivityLabel() {
      return STANDARD_ACTIVITY_LABEL[this.standardActivityType] || null;
    },
    typeActivity() {
      return this.activity || this.standardActivityType ? true : false;
    },
    typeComment() {
      return !this.typeActivity && !this.child;
    },
    typeCommentReply() {
      return !this.typeActivity && this.child;
    },
    // Comment edit/delete (docs/collaboration-comment-edit-delete-proposal.md §7.1): the server
    // decides — `can` is its hint (ownership, admin, archive, windows at read time). The local
    // window tick only stops a button lingering after the window closes while the panel is
    // open; every write is re-checked server-side and refused with a reason (R6).
    isCommentEditable() {
      const can = this.data && this.data.can;
      return Boolean(
        can && can.edit &&
        !this.isCommentDeleted &&
        this.isComment &&
        this.isWithinTimeLimit(this.data.on, "minutes", 15)
      );
    },
    isCommentDeletable() {
      const can = this.data && this.data.can;
      if (!can || !can.delete || this.isCommentDeleted || !this.isComment) {
        return false;
      }

      // an admin's delete has no window (R3); the owner's does
      if (this.data.by !== this.currentUsername) {
        return true;
      }

      return this.isWithinTimeLimit(this.data.on, "hours", 60);
    },
    // P3 admin "view original": Administrator / Workspace Admin (the same set that may delete
    // any comment, R3); the server enforces it again on the read.
    isWorkspaceAdmin() {
      const roles = authorizationStore.getters.userRoles || [];
      return roles.indexOf('Administrator') > -1 || roles.indexOf('Workspace Admin') > -1;
    },
    canViewRevisions() {
      return Boolean(this.data && this.data.hasRevisions && this.isWorkspaceAdmin && this.mode !== 'history');
    },
    // §7.3 copy variants: own deletion, someone else's, and an admin removing another
    // member's comment (names the admin, as WhatsApp does).
    deletedLabel() {
      const deletedBy = this.data && this.data.deletedBy;
      const author = this.data && this.data.by;

      if (deletedBy && deletedBy === this.currentUsername) {
        return author === this.currentUsername
          ? 'You deleted this comment.'
          : `You deleted ${author}'s comment.`;
      }

      if (deletedBy && author && deletedBy !== author) {
        return `This comment was deleted by ${deletedBy}.`;
      }

      return 'This comment was deleted.';
    },
    editedStamp() {
      return this.data && this.data.editedOn
        ? `Edited ${moment(this.data.editedOn).format('lll')}`
        : null;
    },
    channel() {
      if (this.data && this.data.channel)
        switch (this.data.channel) {
          case "ios":
            return {
              name: "iOS App",
              url: commons.iOSAppStoreURL,
            };
          case "android":
            return {
              name: "Android App",
              url: commons.androindPlayStoreURL,
            };
          case "zapier":
            return {
              name: "Zapier",
            };
          case "integromat":
            return {
              name: "Make",
            };
        }
    }
  },
  mounted() {
    this.$nextTick(this.initAiConfidenceRingAnimation);
  },
  beforeDestroy() {
    this.cleanupAiConfidenceRingAnimation();
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-history-content {
  position: relative;
  padding: 0 14px 8px;
  // one scale for the whole history surface, matching the detail panel: 0.78rem body,
  // 0.625rem micro-label. Hierarchy is weight and colour, never a third size.
  font-size: 0.78rem;
  line-height: 1.5;

  // Everything below the actor line aligns to the actor's NAME, not past it: the caret
  // sits in the avatar column, which is dead space on every continuation row. That keeps
  // one clean left edge and spends no width on a gutter of its own.
  //
  // There is deliberately no connecting rail. It belonged to the old design where every
  // row carried its own avatar and the line joined them into a timeline; with one avatar
  // per actor run it connects nothing, and the indent already states the grouping.
  $avatar: 20px;
  $gap: 8px;
  $indent: $avatar + $gap; // 28px — text lands level with the name

  // The fill marks authored text — words a person typed — whether they typed them as a
  // comment or alongside an activity. Both are the same act of writing, so both are boxed.
  //
  // Both modes get this. The Comment tab mixes speech and activity exactly like the
  // History tab does, so gating the fill on mode only left the tab that is *about*
  // comments as the one place a comment did not look like one.
  .history-content-comment {
    padding: 6px 10px;
    border-radius: var(--inistate-rounded-4);
    background-color: var(--inistate-color-neutral-50);
  }

  .history-content__actor {
    display: flex;
    align-items: center;
    padding-top: 10px;
  }

  .history-content__actor-profile {
    align-items: center;

    // the row text below indents by $avatar + $gap (28px); FTSProfile's own avatar-to-name
    // margin is 6px (24px total), 2px short of that — nudge it to keep the name aligned
    // with everything under it, per the actor-line comment above.
    .profile-photo + .profile-name {
      margin-left: $gap;
    }
  }

  .history-content-actor-source {
    margin-left: 6px;
    font-size: 0.625rem;
    line-height: 1rem;
    color: var(--inistate-text-tertiary);

    a {
      color: inherit;
    }
  }

  // event row: [caret gutter] [verb + state] [time / hover actions] [confidence ring]
  .history-content__event {
    display: flex;
    align-items: center;
    gap: $gap;
    padding: 1px 4px 1px 0;
    border-radius: var(--inistate-rounded-4);

    &.history-content__event--expandable {
      cursor: pointer;
    }

    &:hover {
      background-color: var(--inistate-background-secondary);
    }

    // a bare row is an empty strip; a band across it looks like a rendering fault
    &.history-content__event--bare:hover {
      background-color: transparent;
    }
  }

  // A bare row has nothing to show but the clock, so it stops taking a line of its own and
  // rides at the top right, level with the actor's name — the comment then sits directly
  // under the name with no empty band between them. Bare implies not expandable, so the
  // caret gutter it would otherwise reserve is dead width here.
  //
  // Only when the name is actually above it: a continuation row has no actor line to ride
  // on, and there the timestamp is the only thing marking the boundary between two
  // comments, so it stays in flow.
  &:not(.history-content--continues) .history-content__event--bare {
    position: absolute;
    top: 8px;
    right: 4px;
    padding: 0;

    .history-content-event-gutter,
    .history-content-event-main {
      display: none;
    }
  }

  // The actions belong to the ENTRY, not to the strip that happens to hold them. A plain
  // comment's strip is empty, so the thing a reader aims at is the comment itself —
  // scoping the reveal to the whole entry keeps Reply and the rest reachable from there.
  &:hover > .history-content__event {
    .history-content-event-time {
      visibility: hidden;
    }

    .history-content-event-actions {
      visibility: visible;
    }
  }

  // ...and the comment answers the hover itself, one quiet step down, so the entry reads
  // as one object rather than a strip and an unrelated box. Remarks stay unfilled.
  &:hover .history-content-comment {
    background-color: var(--inistate-color-neutral-100);
  }

  // occupies the avatar column, so the caret costs no horizontal space of its own
  .history-content-event-gutter {
    width: $avatar;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--inistate-text-tertiary);
  }

  .history-content-event-caret {
    font-size: 14px;
    transition: transform 0.2s ease;

    .history-content__event--open & {
      transform: rotate(90deg);
    }
  }

  .history-content-event-main {
    flex: 1;
    min-width: 0;
  }

  // The timestamp and the hover actions share ONE grid cell, stacked. The cell sizes to
  // whichever is wider, so the space is always reserved: the actions can never overlap
  // the state badge or the confidence ring the way an absolutely positioned overlay did,
  // and revealing them shifts nothing.
  .history-content-event-aside {
    flex: none;
    display: grid;
    grid-template-areas: "slot";
    align-items: center;
    justify-items: end;
    min-height: 20px;
  }

  .history-content-event-time,
  .history-content-event-actions {
    grid-area: slot;
  }

  .history-content-event-time {
    font-size: 0.625rem;
    line-height: 1rem;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: var(--fts-gray-400);
  }

  .history-content-event-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    visibility: hidden;
  }

  .history-content-event-action {
    display: flex;
    align-items: center;
    color: var(--inistate-icon-secondary);
    @include user-select-none;

    > span {
      font-size: 16px;
    }

    &:hover {
      color: var(--inistate-text-primary);
      cursor: pointer;
    }
  }

  // hover does not exist on touch, so the actions stay in flow and always visible there
  // rather than hiding behind an interaction that cannot happen
  // hover does not exist on touch, so the actions take the shared cell permanently there
  // rather than hiding behind an interaction that cannot happen. The full timestamp is
  // still reachable — it is in the row's tooltip.
  @media (hover: none) {
    .history-content-event-time {
      visibility: hidden;
    }

    .history-content-event-actions {
      visibility: visible;
    }
  }

  .history-content-change-count {
    margin-left: 4px;
    font-size: 0.625rem;
    font-variant-numeric: tabular-nums;
    color: var(--inistate-text-tertiary);
  }

  // comments, reactions and the detail panel all hang off the same left edge as the
  // event text and the actor name above it
  .history-content__body,
  .history-content__footer,
  .history-content__detail {
    margin-left: $indent;
  }

  .history-content__body,
  .history-content__footer {
    margin-top: 3px;
  }

  // One colour budget (SS05902): people are weight, never hue. The medium weight was
  // already carrying the name; the blue on top of it only suggested a link that isn't
  // one — the name's single behaviour is a username tooltip. Dropping it leaves the
  // state chip as the one coloured thing on the panel, which is the one that means
  // something.
  .history-content-by {
    font-weight: var(--inistate-font-weight-medium);
    color: var(--inistate-text-primary);
  }

  .history-content-activity {
    color: var(--inistate-text-secondary);

    &.history-content-activity-title {
      font-weight: var(--inistate-font-weight-medium);
      color: var(--inistate-text-primary);
    }
  }

  // weight, not hue (SS05902): the preparer's name reads like any other name on the
  // line and only shows it is a link on hover
  .history-content-approval-link {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .history-content-activity-ai {
    height: 20px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    vertical-align: top;
    span {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 14px;
      width: 18px;
      border-radius: var(--inistate-rounded-4);
      background: linear-gradient(131.74deg, #5497d4 9.27%, #db0e8d 100%);
      color: #fff;
      font-size: var(--inistate-label-text-10);
      line-height: var(--inistate-label-text-10--line-height);
    }

    // TeamMate variant: the GNey mark is a logo, so it stands on its own rather than
    // being boxed inside the pill — the house idiom for a branded glyph (see
    // fts-icon-magic-lamp + inistate-text-ai). The "AI" pill stays for other surfaces.
    &.history-content-activity-ai--teammate > i {
      font-size: 16px;
      line-height: 1;
    }
  }

  .history-content-state {
    padding-top: 2px;
    padding-bottom: 2px;
    border-radius: var(--inistate-rounded-4);
    > div {
      font-weight: var(--inistate-font-weight-light);
      font-size: var(--inistate-label-text-12);
      line-height: var(--inistate-label-text-12--line-height);
    }
  }

  .history-content-action-container {
    display: flex;
    align-items: center;
  }

  // sits with the other row actions in the shared cell, so it matches their 16px metric
  // rather than the 36px hit area it had as a standalone header control
  .history-content-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 16px;
    width: 16px;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    color: var(--inistate-icon-secondary);
    transition: background-color 0.3s ease, color 0.3s ease;
    @include user-select-none;

    > span {
      font-size: 16px;
    }

    &:not(&--active):not(&--disabled):hover {
      background-color: var(--inistate-background-secondary);
      color: var(--inistate-icon-primary);
      cursor: pointer;
    }

    &--active {
      background-color: var(--inistate-background-secondary);
      color: var(--inistate-icon-primary);
    }
  }

  .history-content-attachment {
    .file-wrapper {
      max-width: calc(50% - 0.25rem);
      
      .file-info {
        .file-name {
          font-size: var(--inistate-label-text-14);
          line-height: var(--inistate-paragraph-text-14--line-height);
          color: var(--inistate-text-primary);
        }
        .file-size {
          font-size: var(--inistate-label-text-12);
          color: var(--inistate-text-secondary);
        }
      }
    }
  }

  .history-content-comment {
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    color: var(--inistate-text-primary);
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: pre-wrap;

    // tombstone (§7.3): italic secondary + 14px block glyph, same slot, no chrome
    &--deleted {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-style: italic;
      color: var(--inistate-text-secondary);

      .history-content-comment-deleted-icon {
        font-size: 14px;
        font-style: normal;
      }
    }

    .history-content-comment-edited {
      color: var(--inistate-text-secondary);
      cursor: default;
    }

    .history-content-revisions-link {
      margin-left: 8px;
      font-style: normal;
      font-size: var(--inistate-label-text-12);
      color: var(--inistate-text-link, #2b9ec6);
      cursor: pointer;
    }
  }

  // P3 admin "view original" — calm inline block under the bubble, same radius/border as a section
  .history-content__revisions {
    margin-top: 8px;
    padding: 8px 10px;
    border: 1px solid var(--fts-border);
    border-radius: var(--inistate-rounded-4);
    background-color: var(--inistate-color-neutral-50);
    font-size: var(--inistate-label-text-12);

    .history-content-revisions-header {
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--inistate-text-secondary);
      margin-bottom: 6px;

      > .material-icons-outlined {
        font-size: 16px;
      }

      .history-content-revisions-title {
        flex: 1 1 auto;
        font-weight: 500;
      }

      .history-content-revisions-close {
        cursor: pointer;
      }
    }

    .history-content-revision + .history-content-revision {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--fts-border);
    }

    .history-content-revision-caption {
      color: var(--inistate-text-secondary);
      margin-bottom: 2px;
    }

    .history-content-revision-text {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    .history-content-revision-attachments,
    .history-content-revisions-loading {
      color: var(--inistate-text-secondary);
    }
  }

  .history-content-confidence-tip {
    display: inline-flex;
    vertical-align: middle;
    margin-left: 4px;
    flex: none;
  }

  // sized for the compact event row: the ring is a read-out beside the timestamp, not a
  // 36px-tall control as it was when it lived in the old header
  .history-content-confidence {
    display: flex;
    align-items: center;
    @include user-select-none;

    .history-content-confidence-value {
      position: relative;
      z-index: 1;
      font-weight: var(--inistate-font-weight-medium);
      font-size: 0.625rem;
      line-height: 1;

      &.history-content-confidence-value--compact {
        font-size: 0.5625rem;
      }
    }

    .history-content-confidence-marks {
      position: relative;
      height: 22px;
      width: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .history-content-confidence-ring {
      position: absolute;
      inset: 0;
      transform: rotate(-90deg);
      z-index: 0;
    }

    .history-content-confidence-ring-track,
    .history-content-confidence-ring-progress {
      fill: none;
      stroke-width: 3;
    }

    .history-content-confidence-ring-track {
      stroke: var(--inistate-border-primary);
    }

    .history-content-confidence-ring-progress {
      stroke-linecap: round;
      transition: stroke 0.3s ease, stroke-dashoffset 0.8s ease;
    }

    @media (prefers-reduced-motion: reduce) {
      .history-content-confidence-ring-progress {
        transition: none;
      }
    }
  }

  .history-content-confidence-icon {
    margin-right: 4px;
    font-size: 18px;
  }

  .history-content-ai-toggle {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--inistate-icon-secondary);

    > span {
      font-size: 20px;
      transition: transform 0.3s ease;
    }

    &.history-content-ai-toggle--active {
      > span {
        transform: rotate(-180deg);
      }
    }
  }

  // One expandable panel per history item: the AI summary (header + reasoning + sources)
  // and the calm-density change log share this box — either toggle opens it.
  //
  // Typography is deliberately flat. The whole panel runs on the change log's scale
  // (0.78rem body / 0.625rem micro-label, set once here and inherited) so the AI half
  // and the log half read as one surface instead of two pasted-together designs.
  // Hierarchy comes from weight and colour — never from a third font size.
  .history-content__detail {
    position: relative;
    z-index: 1; // sit on top of the timeline connecting line, not under it
    margin-top: 8px;
    border: 1px solid var(--inistate-border-primary);
    border-radius: var(--inistate-rounded-8);
    background-color: var(--inistate-background-primary);
    font-size: 0.78rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
    word-break: break-word;

    // the model line and the reasoning are one statement — "this model concluded this" —
    // so the AI section is a single padded block with no rule inside it. The only division
    // left in the panel is AI section vs change log, which is the real one.
    .history-content__ai {
      padding: 10px 12px;
    }

    .history-content-ai-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 6px;
    }

    .history-content-ai-icon {
      height: 14px;
      width: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      // fts-font glyph (TeamMate): size it to the box, not the inherited body size
      > i {
        font-size: 14px;
        line-height: 1;
      }
    }

    .history-content-ai-model {
      flex: 1;
      min-width: 0; // let the flex item shrink below its content width, so the ellipsis engages
    }

    .history-content-ai-model-text {
      display: block;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-weight: var(--inistate-font-weight-medium);
      color: var(--inistate-text-secondary);

      > span {
        margin-left: 4px;
        font-weight: var(--inistate-font-weight-light);
        color: var(--inistate-text-tertiary);
      }
    }

    .history-content-ai-reason {
      margin-bottom: 10px;
      color: var(--inistate-text-secondary);

      span {
        font-weight: var(--inistate-font-weight-medium);
        color: var(--inistate-text-primary);
      }
    }

    // micro-label, same treatment as the change log's provenance tags
    .history-content-ai-source-legend {
      font-size: 0.625rem;
      line-height: 1rem;
      font-weight: var(--inistate-font-weight-medium);
      letter-spacing: 0.02em;
      color: var(--inistate-text-tertiary);
    }

    .history-content-ai-source-container {
      margin-top: 4px;
      .history-content-ai-source:not(:first-child) {
        margin-top: 6px;
      }
    }

    // A source is a badge column and a content column — the same field | value idiom
    // the change log uses, so the two halves of the panel align on the same grid.
    // No card border or background: the panel already provides the box, and it was
    // the box-in-box that made this section loud. The excerpt indents under its own
    // reference, which is what the border was really doing.
    .history-content-ai-source {
      display: grid;
      // the 3.5rem floor is the change log's key-column width: it makes every source's
      // reference start at the same x regardless of how wide its badge is, and lines the
      // sources up with the log's field names directly below them
      grid-template-columns: minmax(3.5rem, max-content) 1fr;
      column-gap: 0.75rem;
      row-gap: 0.1rem;
      align-items: baseline;

      &.history-content-ai-source--code {
        .history-content-ai-source-icon {
          color: var(--inistate-badge-dark-blue);
        }

        .history-content-ai-source-badge {
          background-color: var(--inistate-badge-dark-blue);
        }
      }
    }

    .history-content-ai-source-header {
      grid-column: 1;
      grid-row: 1;
      display: flex;
      align-items: center;
    }

    .history-content-ai-source-icon {
      height: 12px;
      width: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 4px;
      color: var(--inistate-badge-dark-orange);

      > span {
        font-size: 12px;
      }
    }

    .history-content-ai-source-badge {
      display: inline-flex;
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      border-radius: 0.15rem;
      background-color: var(--inistate-badge-dark-orange);
      color: #fff;
      font-size: 0.625rem;
      line-height: 1rem;
      letter-spacing: 0.02em;
      text-transform: uppercase;
    }

    .history-content-ai-source-reference {
      grid-column: 2;
      grid-row: 1;
      min-width: 0;
      font-weight: var(--inistate-font-weight-medium);
      color: var(--inistate-text-primary);
    }

    .history-content-ai-source-excerpt {
      grid-column: 2;
      min-width: 0;
      color: var(--inistate-text-secondary);
    }

    // calm-density change log (SS05902) — content untouched; the panel provides the box
    .history-content__changes {
      padding: 10px 12px;
    }
    .history-content__ai + .history-content__changes {
      border-top: 1px solid var(--inistate-color-neutral-100);
    }
  }

  // a mention is a person, so it follows the actor-name rule above: weight, not hue. The
  // "@" is already the marker that this is someone rather than a word.
  .history-content-mention {
    font-weight: var(--inistate-font-weight-medium);
    color: var(--inistate-text-primary);
  }

  // the picker now lives in the event row's hover actions, sized to sit with them
  .history-content-reaction-toggle {
    display: inline-flex;
    @include user-select-none;
  }

  .history-content-reaction-toggle-ref {
    display: flex;
    align-items: center;

    > span {
      font-size: 14px;
      color: var(--inistate-icon-secondary);
    }

    &:hover {
      cursor: pointer;
      > span {
        color: var(--inistate-text-primary);
      }
    }
  }

  .history-content-reaction-gap {
    @include flex-gap($display: flex, $gap: 6px);
    align-items: center;
  }

  .history-content-reaction-item {
    &.history-content-reaction-item--active {
      .history-content-reaction {
        background-color: var(--inistate-color-primary-100);
      }
    }
  }

  .history-content-reaction {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border-radius: var(--inistate-rounded-20);
    background-color: var(--inistate-badge-light-grey);
    @include user-select-none;
    &:hover {
      cursor: pointer;
    }
  }

  .history-content-reaction-count {
    margin-left: 4px;
    font-weight: var(--inistate-font-weight-light);
    font-size: var(--inistate-label-text-12);
    line-height: var(--inistate-label-text-12--line-height);
    color: var(--inistate-text-primary);
  }

}
// temp (for dark mode)
body.dark {
  .fts-history-content {
    .history-content-comment,
    .history-content-activity.history-content-activity-title,
    // names and mentions carry ink now rather than the theme-independent blue, so they
    // need the dark counterpart every other --inistate-text-primary user here has
    .history-content-by,
    .history-content-mention {
      color: var(--fts-text);
    }

    .history-content-event-action:hover {
      color: var(--fts-gray-700);
    }

    .history-content-event-actions {
      background-color: var(--fts-gray-100);
    }

    .history-content__event:hover {
      background-color: var(--fts-gray-100);
    }

    .history-content__event.history-content__event--bare:hover {
      background-color: transparent;
    }

    &:hover .history-content-comment {
      background-color: var(--fts-gray-200);
    }

    .history-content-reaction {
      background-color: var(--fts-gray-100);
      .history-content-reaction-count {
        color: var(--fts-gray-700);
      }
    }

    .history-content-reaction-item {
      &.history-content-reaction-item--active {
        .history-content-reaction {
          background-color: var(--fts-primary-900);
        }
      }
    }

    .history-content-reaction-toggle-ref:hover > span {
      color: var(--fts-gray-700);
    }

    .history-content-comment {
      background-color: var(--fts-gray-100);
    }

    .dropdown-option.dropdown-option--lite {
      color: var(--fts-text);

      &.dropdown-option--danger {
        color: var(--fts-danger);

        &:hover {
          color: var(--fts-danger);
        }
      }

      &:hover {
        color: var(--fts-dark);
      }
    }

    .history-content-action-btn {
      &:not(&--active):not(&--disabled):hover,
      &--active {
        background-color: var(--fts-gray-100);
        color: var(--fts-gray-700);
      }
    }

    .history-content__detail {
      border-color: var(--fts-border);
      background-color: var(--fts-background);

      .history-content__ai + .history-content__changes {
        border-color: var(--fts-border);
      }
    }

    .history-content-confidence:hover {
      background-color: var(--fts-gray-100);
    }
  }

  .history-content-reaction-dropdown .history-content-reaction-option:hover {
    background-color: var(--fts-gray-100);
  }
}
// reaction
.history-content-reaction-dropdown {
  display: flex;
  padding: 4px;
  .history-content-reaction-option {
    height: 24px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    transition: background-color 0.3s ease, color 0.3s ease;
    @include user-select-none;

    &:hover {
      background-color: var(--inistate-color-neutral-100);
      cursor: pointer;
    }
  }
}
// tippy (reaction)
.tippy-box-history-reaction {
  padding: 1rem;
  .tippy-content-history-reaction {
    display: flex;
    align-items: center;
    .history-reaction-emoji {
      margin-right: 1.25rem;
    }
    .history-reaction-user-container {
      @include flex-gap($display: flex, $gap: 0.375rem);
      align-items: center;
    }
    .profile-name {
      color: var(--fts-light);
    }
  }
}
</style>
